import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePremium } from '../contexts/PremiumContext';
import html2canvas from 'html2canvas';
import '../styles/StoryGenerator.css';

const storyThemes = [
  { key: 'simple', name: 'شب یاسی', kicker: 'یک چالش دوستانه', emoji: '✦', className: 'lilac', premium: false, image: 'https://images.unsplash.com/photo-1511632765486-a01980e01a18?auto=format&fit=crop&w=900&q=85' },
  { key: 'gradient_purple', name: 'شفق قطبی', kicker: 'جرأت داری امتحانش کنی؟', emoji: '☄', className: 'aurora', premium: false, image: 'https://images.unsplash.com/photo-1531058020387-3be344556be6?auto=format&fit=crop&w=900&q=85' },
  { key: 'colorful', name: 'پاپ‌آرت', kicker: 'رنگی، جسور و متفاوت', emoji: '●', className: 'pop', premium: false, image: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=900&q=85' },
  { key: 'minimal_dark', name: 'نئون شب', kicker: 'رکوردت را ثبت کن', emoji: '⌁', className: 'neon', premium: true, image: 'https://images.unsplash.com/photo-1511988617509-a57c8a288659?auto=format&fit=crop&w=900&q=85' },
  { key: 'pastel', name: 'یادداشت عشق', kicker: 'برای آدم‌های نزدیک', emoji: '♡', className: 'love-note', premium: true, image: 'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?auto=format&fit=crop&w=900&q=85' },
  { key: 'futuristic', name: 'فضای آینده', kicker: 'برای یک چالش تازه', emoji: '◌', className: 'cosmic', premium: true, image: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=900&q=85' }
];

const typeLabels = {
  friend: 'دوستانه', newFriend: 'آشنایی تازه', family: 'خانوادگی', spouse: 'عاشقانه',
  colleague: 'همکاران', crush: 'کراش', classmate: 'هم‌کلاسی', follower: 'فالوورها'
};

function StoryGenerator({ testUuid, creatorName, testType, questionsCount, onClose }) {
  const { isPremium } = usePremium();
  const [remoteTemplates, setRemoteTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('simple');
  const [generating, setGenerating] = useState(false);
  const storyRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL || '/backend';

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/story/templates.php`);
        const data = await response.json();
        if (data.success && Array.isArray(data.templates)) setRemoteTemplates(data.templates);
      } catch (error) {
        console.info('Story templates are using local fallbacks.', error);
      }
    };
    fetchTemplates();
  }, [apiUrl]);

  const templates = useMemo(() => storyThemes.map((theme) => {
    const remote = remoteTemplates.find((item) => item.template_key === theme.key);
    return { ...theme, name: remote?.name || theme.name, premium: remote?.is_premium ?? theme.premium };
  }), [remoteTemplates]);

  const currentTheme = templates.find((template) => template.key === selectedTemplate) || templates[0];

  const selectTemplate = (template) => {
    setSelectedTemplate(template.key);
  };

  const generateStory = async () => {
    if (!storyRef.current) return;
    if (currentTheme.premium && !isPremium) {
      alert('پیش‌نمایش این قالب آزاد است؛ برای دانلود آن، نسخه حرفه‌ای را فعال کن.');
      return;
    }
    setGenerating(true);
    try {
      await Promise.all([
        document.fonts?.load('900 100px Vazirmatn'),
        document.fonts?.load('700 40px Vazirmatn'),
        document.fonts?.ready
      ]);
      storyRef.current.classList.add('is-exporting');
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const canvas = await html2canvas(storyRef.current, {
        width: 1080, height: 1920, scale: 1, backgroundColor: null,
        logging: false, useCORS: true, allowTaint: true,
        windowWidth: 1080, windowHeight: 1920
      });
      storyRef.current.classList.remove('is-exporting');
      canvas.toBlob(async (blob) => {
        if (!blob) throw new Error('Story image was not created');
        const file = new File([blob], `story-${testUuid}.png`, { type: 'image/png' });
        if (navigator.share && navigator.canShare?.({ files: [file] })) {
          try {
            await navigator.share({ files: [file], title: 'چقدر منو می‌شناسی؟', text: `ببین ${creatorName} رو چقدر می‌شناسی!` });
            setGenerating(false);
            return;
          } catch (error) {
            if (error.name === 'AbortError') { setGenerating(false); return; }
          }
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url; link.download = `story-cheghadr-${Date.now()}.png`;
        document.body.appendChild(link); link.click(); link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 200);
        setGenerating(false);
      }, 'image/png', 1);
    } catch (error) {
      storyRef.current?.classList.remove('is-exporting');
      console.error('Generate story error:', error);
      alert('ساخت استوری با خطا روبه‌رو شد.');
      setGenerating(false);
    }
  };

  return createPortal((
    <div className="story-generator-overlay" onClick={onClose}>
      <section className="story-generator-modal" onClick={(event) => event.stopPropagation()}>
        <button className="story-close" onClick={onClose} aria-label="بستن"><span className="material-symbols-outlined">close</span></button>
        <header className="story-studio-header">
          <span className="story-studio-eyebrow">STORY STUDIO</span>
          <h2>استوریِ دعوت به چالش</h2>
          <p>یک قالب انتخاب کن؛ تصویر آماده انتشار است.</p>
        </header>

        <div className="story-studio-layout">
          <aside className="story-theme-panel">
            <div className="story-panel-title"><span>قالب‌ها</span><small>{templates.length} استایل</small></div>
            <div className="story-theme-list">
              {templates.map((template) => (
                <button key={template.key} className={`story-theme-card ${template.className} ${selectedTemplate === template.key ? 'is-selected' : ''}`} onClick={() => selectTemplate(template)}>
                  <span className="story-theme-swatch"><i /><b>{template.emoji}</b></span>
                  <span><strong>{template.name}</strong><small>{template.kicker}</small></span>
                  {template.premium && <em>PRO</em>}
                </button>
              ))}
            </div>
            {!isPremium && <div className="story-pro-hint"><span className="material-symbols-outlined">auto_awesome</span> سه قالب ویژه در نسخه حرفه‌ای باز می‌شوند.</div>}
          </aside>

          <div className="story-preview-shell">
            <span className="story-preview-label">پیش‌نمایش 9:16</span>
            <div className="story-preview-container">
              <div className="story-preview-stage">
              <div ref={storyRef} className={`story-canvas story-theme-${currentTheme.className}`}>
                <div className="story-orb story-orb-one" /><div className="story-orb story-orb-two" /><div className="story-grid-lines" />
                <div className="story-content">
                  <div className="story-topline"><span className="story-logo-dot">؟</span><span className="story-wordmark"><i>چقدر</i><i>منو</i><i>می‌شناسی</i></span><b>{currentTheme.emoji}</b></div>
                  <div className="story-main-copy">
                    <span className="story-kicker">{typeLabels[testType] || 'چالش شخصی'}</span>
                    <h1 className="story-title-lines"><span>فکر می‌کنی</span><span><i>منو</i><i>خوب</i><i>می‌شناسی؟</i></span></h1>
                    <p><strong>{creatorName || 'دوستت'}</strong> یه چالش کوتاه برات ساخته.</p>
                  </div>
                  <div className="story-question-card"><span>؟</span><p>چند تا جواب درست می‌دی؟</p><b>شروع کن</b></div>
                  <div className="story-photo-frame"><img src={currentTheme.image} crossOrigin="anonymous" alt="لحظه‌ای دوستانه از چالش" onError={(event) => { event.currentTarget.parentElement.style.display = 'none'; }} /><span>{currentTheme.emoji}</span></div>
                  <div className="story-meta-row"><div><b>{questionsCount || 10}</b><span>سؤال</span></div><i /><div><b>۲</b><span>دقیقه</span></div><i /><div><b>∞</b><span>هیجان</span></div></div>
                  <div className="story-footer"><span>cheghadrmishnasi.com</span><b>لینک در بیو ↗</b></div>
                </div>
                {currentTheme.premium && !isPremium && <div className="story-premium-preview"><span className="material-symbols-outlined">lock</span><b>پیش‌نمایش قالب حرفه‌ای</b><small>برای دانلود، پریمیوم را فعال کن</small></div>}
              </div>
              </div>
            </div>
          </div>
        </div>

        <footer className="story-actions">
          <p><span className="material-symbols-outlined">ios_share</span> در موبایل، دکمه انتشار مستقیم باز می‌شود.</p>
          <button className="btn-generate-story" onClick={generateStory} disabled={generating}><span className="material-symbols-outlined">{generating ? 'hourglass_top' : currentTheme.premium && !isPremium ? 'lock' : 'download'}</span>{generating ? 'در حال ساخت…' : currentTheme.premium && !isPremium ? 'دانلود با نسخه حرفه‌ای' : 'ساخت و دانلود استوری'}</button>
        </footer>
      </section>
    </div>
  ), document.body);
}

export default StoryGenerator;
